import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '~/components/isolated/Button/Button';
import { IconButton } from '~/components/isolated/IconButton';
import Modal from '~/components/Modal/Modal';
import { useGlobal } from '~/hooks/useGlobal';
import { useModal } from '~/hooks/useModal';

import { checkoutStore, mainStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';

import styles from './ModalChangeEmailV3.module.scss';

export default observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();

  const [step, setStep] = useState(0);
  const [emailVal, setEmailVal] = useState<string>(
    () => userStore.personalData.email || checkoutStore.emailVal,
  );
  const [isTouched, setIsTouched] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { isMobile } = useGlobal();

  const backHandler = () => closeModal();

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailVal(e.currentTarget.value);
  };

  const updateUserEmail = () => {
    setHasAttemptedSubmit(true);
    if (!isEmailValid) {
      return;
    }

    const value = emailVal.trim();

    if (value === userStore.personalData.email) {
      return;
    }

    checkoutStore.setIsEmailSync(false);
    checkoutStore.setIsPersonalDataChanged(true);
    checkoutStore.setIsPersonalDataSync(false);

    userStore
      .updatePersonalEmail(value)
      .then((flag) => {
        checkoutStore.setIsEmailSync(flag);
        checkoutStore.setIsPersonalDataSync(flag);

        if (flag) {
          setStep(1);
        }
      })
      .catch((error) => error && console.error(error));
  };

  useEffect(() => {
    checkoutStore.setEmailVal(userStore.personalData.email || '');
  }, []);

  const isEmailValid = mainStore.validateEmail(emailVal);

  return (
    <Modal
      isShow={true}
      className={classes([styles.modal, step === 1 && styles.success])}
      onBackdropDismiss={backHandler}
    >
      {step === 1 ? (
        <div className={styles.successContent}>
          <Icon type="check-round-3.1" size={36} />
          <h2 className={styles.successTitle}>{t('phrases:emailChangedV3')}</h2>
          <Button variant="primary" size="L" onClick={backHandler}>
            {t('great')}
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <h2 className={styles.title}>{t('changeEmail')}</h2>
              <IconButton
                variant="outline"
                size="S"
                icon="close"
                onClick={backHandler}
                className={styles.closeButton}
              />
            </div>
          </div>
          <div className={styles.content}>
            <p className={styles.subtitle}>{t('phrases:enterNewEmail')}</p>
            <div className={styles.form}>
              <input
                type="email"
                className={styles.input}
                value={emailVal}
                onChange={handleEmailChange}
                onBlur={() => setIsTouched(true)}
                disabled={userStore.emailChangeMeta.isLoading}
                autoFocus
              />
              {!!(
                emailVal.length &&
                !isEmailValid &&
                (isTouched || hasAttemptedSubmit)
              ) && <p className={styles.error}>{t('phrases:invalidEmail')}</p>}
            </div>
            <div className={styles.buttons}>
              <Button variant="secondary" size="L" onClick={backHandler}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary"
                size={isMobile ? 'L' : 'XL'}
                onClick={updateUserEmail}
                disabled={!isEmailValid || userStore.emailChangeMeta.isLoading}
                isLoading={userStore.emailChangeMeta.isLoading}
              >
                {t('confirm')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
});
